import { baseUrl } from "../config/apiConfig";
import { getDataFromLocalStorage } from "../util/localStorage";

const connections = new Map();
const configuredBase = process.env.REACT_APP_NOTIFICATION_URL;
const notificationBase = configuredBase || (
  /^http:\/\/(localhost|127\.0\.0\.1):8000\/?$/.test(baseUrl)
    ? baseUrl.replace(":8000", ":8003")
    : baseUrl
);

export async function readNotificationStream(response, onEvent, signal) {
  if (!response.ok) {
    const error = new Error(`Notification stream returned HTTP ${response.status}`);
    error.status = response.status;
    throw error;
  }
  if (!response.headers.get("content-type")?.includes("text/event-stream") || !response.body) {
    throw new Error("Notification endpoint did not return an SSE stream");
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (!signal.aborted) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let boundary;
      while ((boundary = /\r?\n\r?\n/.exec(buffer))) {
        const frame = buffer.slice(0, boundary.index);
        buffer = buffer.slice(boundary.index + boundary[0].length);
        const data = frame.split(/\r?\n/)
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trimStart()).join("\n");
        if (data) onEvent(JSON.parse(data));
      }
      if (buffer.length > 65536) throw new Error("Notification frame exceeded size limit");
    }
  } finally {
    await reader.cancel();
    reader.releaseLock();
  }
}

export function subscribeToNotifications(botId, listener) {
  let connection = connections.get(botId);
  if (!connection) {
    const listeners = new Set();
    const controller = new AbortController();
    let retryTimer;
    let retryDelay = 1000;
    let lastEventId;
    const connect = async () => {
      const token = getDataFromLocalStorage("token");
      if (!token || controller.signal.aborted) return;
      let retry = true;
      try {
        const response = await fetch(
          `${notificationBase.replace(/\/$/, "")}/notifications/bots/${encodeURIComponent(botId)}/stream`,
          {
            headers: { Authorization: `Bearer ${token}`, "ngrok-skip-browser-warning": "true" },
            signal: controller.signal,
          }
        );
        await readNotificationStream(response, (event) => {
          if (event.type !== "connected" && event.type !== "resync" && String(event.bot_id) !== String(botId)) return;
          if (event.event_id && event.event_id === lastEventId) return;
          lastEventId = event.event_id;
          retryDelay = 1000;
          listeners.forEach((callback) => callback(event));
        }, controller.signal);
        if (!controller.signal.aborted) throw new Error("Notification stream disconnected");
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("Live notifications unavailable; periodic refresh remains active:", error);
        retry = ![401, 403, 404].includes(error.status);
      }
      if (retry && !controller.signal.aborted) {
        retryTimer = setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 30000);
      }
    };
    connection = {
      listeners,
      close: () => { controller.abort(); clearTimeout(retryTimer); },
    };
    connections.set(botId, connection);
    // Defer until the first listener has been registered.
    Promise.resolve().then(connect);
  }
  connection.listeners.add(listener);
  return () => {
    connection.listeners.delete(listener);
    if (!connection.listeners.size) {
      connection.close();
      connections.delete(botId);
    }
  };
}
